//================================================================
//  Page: Home
//================================================================

//  Purpose: Home page for this application

//  Supporting documentation
//    - N/A

//================================================================

//Libraries
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components
import PageComponent from '../../Components/PageComponent/PageComponent';
import NewInitiative from './Forms/NewInitiative';
import ContractRenewal from './Forms/ContractRenewal';
import Enhancement from './Forms/Enhancement';
import AdditionalLicense from './Forms/AdditionalLicense';
import Decommission from './Forms/Decommission';

//Functions

//Images
import ArrowIcon from '../../Components/Images/Icon_FormArrow_Grey.svg';

//CSS
import './Home.css';

export default function Home() {

  //------------------------------------------------------
  //  useContexts & React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const navigate = useNavigate();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Controller for which form is showing > NewInitiative, ContractRenewal, Enhancement, AdditionalLicenses, Decommission
  const [form, setForm] = useState('');

  // Contains data user submitted in the form
  const [formData, setFormData] = useState(undefined);

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState('onload');

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  const getFormat = (formTitle) => {

    // Default state - no form is showing
    if (form === '') return 'bg-white flex flex-col gap-2 rounded-[6px] w-[32%] h-32 p-6 overflow-hidden shadow-md border-solid border-1 border-neutral-200 cursor-pointer';

    // Selected state
    else if (form === formTitle) return 'bg-white flex flex-col gap-2 rounded-[6px] w-[32%] h-32 p-6 overflow-hidden shadow-md border-solid border-1 border-[#0972D3] cursor-pointer';

    // Disabled state
    else return 'bg-neutral-100 flex flex-col gap-2 rounded-[6px] w-[32%] h-32 p-6 overflow-hidden shadow-md border-solid border-1 border-neutral-200 cursor-pointer';

  }

  const showForm = () => {

    if (form === 'NewInitiative') return <NewInitiative setPageStatus={setPageStatus} setFormData={setFormData}></NewInitiative>;

    else if (form === 'ContractRenewal') return <ContractRenewal setPageStatus={setPageStatus} setFormData={setFormData}></ContractRenewal>;

    else if (form === 'Enhancement') return <Enhancement setPageStatus={setPageStatus} setFormData={setFormData}></Enhancement>;

    else if (form === 'AdditionalLicenses') return <AdditionalLicense setPageStatus={setPageStatus} setFormData={setFormData}></AdditionalLicense>;

    else if (form === 'Decommission') return <Decommission setPageStatus={setPageStatus} setFormData={setFormData}></Decommission>;

  }

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      header={
        <div className='flex flex-col p-10 gap-1 w-full border-solid border-1 border-neutral-300'>
          <h4 className='h-full cursor-default text-[22px]'>
            Raise a Digital Request
          </h4>
          <p className='text-[#424242] font-sans text-[16px] leading-[24px] m-0'>Use this form to request approval for a Digital project, contract renewal or application enhancement.
          </p>
        </div>
      }
      body={
        <div className='flex flex-col p-4 gap-4 bg-neutral-100 w-full border-solid border-1 border-neutral-300'>

          {/* Select Form Container */}
          <div className='relative bg-white rounded-[10px] px-8 py-6 w-full shadow-md border-solid border-1 border-neutral-300'>

            <h6 className='font-semibold mb-2 my-1'>What type of request is this?</h6>

            <div className='hidden md:flex flex-wrap gap-3 py-3 place-content-between'>

              {/* New Initiative */}
              <div className={getFormat('NewInitiative')} onClick={() => setForm('NewInitiative')}>
                <p className='font-semibold mb-0'>New Initiative or Technology</p>
                <p className='line-clamp-2 mb-0'>Launch a new Digital initiative, trial or onboard a new technology.</p>
              </div>

              {/* Contract Renewal */}
              <div className={getFormat('ContractRenewal')} onClick={() => setForm('ContractRenewal')}>
                <p className='font-semibold mb-0'>Contract Renewal</p>
                <p className='line-clamp-2 mb-0'>Renew or update an existing Digital related contract.</p>
              </div>

              {/* Enhancement */}
              <div className={getFormat('Enhancement')} onClick={() => setForm('Enhancement')}>
                <p className='font-semibold mb-0 line-clamp-1'>Existing Technology: Enhance, Maintain, or Service</p>
                <p className='line-clamp-2 mb-0'>Upgrade, extend, improve or request a service for an existing technology.</p>
              </div>

              {/* Additional Licenses */}
              <div className={getFormat('AdditionalLicenses')} onClick={() => setForm('AdditionalLicenses')}>
                <p className='font-semibold mb-0'>Additional Licenses or Overages</p>
                <p className='line-clamp-2 mb-0'>Procure more of an application or technology outside of the standard renewal cycle.</p>
              </div>

              {/* Decommission */}
              <div className={getFormat('Decommission')} onClick={() => setForm('Decommission')}>
                <p className='font-semibold mb-0'>Decommission</p>
                <p className='line-clamp-2 mb-0'>Retire an application that is no longer needed.</p>
              </div>

              {/* Placeholder */}
              <div className=' w-[32%]'></div>

            </div>

            {/* Mobile - Show Dropdown */}
            <div className='md:hidden'>
              <select className='Input-Field-Select w-full' onChange={(e) => setForm(e.target.value)}>
                <option hidden value=''>Select</option>
                <option value='NewInitiative'>New Initiative or Technology</option>
                <option value='ContractRenewal'>Contract Renewal</option>
                <option value='Enhancement'>Existing Technology: Enhance, Maintain, or Service</option>
                <option value='AdditionalLicenses'>Additional Licenses or Overages</option>
                <option value='Decommission'>Decommission</option>
              </select>
            </div>

            {/* Arrow (appears when option selected) */}
            <div className='absolute z-50 -bottom-9 left-[42%] md:left-[45%] ' hidden={form === ''}>
              <img src={ArrowIcon} alt='Arrow' className='w-[50px] border border-1 rounded-full'></img>
            </div>

          </div>

          {/* =============================== */}
          {/*          REQUEST FORM           */}
          {/* =============================== */}

          <div className='bg-white rounded-[10px] p-6 w-full shadow-md border-solid border-1 border-neutral-300' hidden={form === ''}>

            {/* Gets selected form */}
            {showForm()}

          </div>

        </div>
      }
      successContent={
        <div className='flex flex-col p-6 gap-4 bg-neutral-100 w-full border-solid border-1 border-neutral-300'>

          <div className='bg-[#e7f2da] p-4 flex flex-col gap-2 rounded-md shadow-md border-l-8 border-l-[#8ABB45]'>
            <label className='font-semibold text-lg'>New Digital Request Submitted</label>
            <p className='text-neutral-600'>
              <strong>{getUser?.givenname} {getUser?.surname} </strong> has submitted {['a', 'e', 'i', 'o', 'u'].includes.formData?.requesttype[0] ? 'an' : 'a'} {formData?.requesttype} request on {formData?.createddate?.toLocaleString()}. View <Link to={{ pathname: `/requests/${formData?.parentrequestid}` }}>{formData?.parentrequestid?.toUpperCase()}</Link>
            </p>
          </div>

          <div className='flex gap-2 flex-col md:flex-row'>

            <button className='Primary-Button' onClick={() => {
              setForm('');
              setPageStatus('onload');
              navigate('/requests');
            }}>
              Go to My Requests
            </button>

            <button className='Secondary-Button' onClick={() => {
              setForm('');
              setPageStatus('onload');
            }}>
              Submit another request
            </button>

          </div>
        </div>
      }
    ></PageComponent>
  );
}
